import { useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import useUsers from '../../hooks/useUsers';

export default function AuthModal({ show, onClose }) {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const { signIn, signUp, signInWithGoogle, resetPassword, error } = useAuthContext();
  const { createUser } = useUsers();

  if (!show) return null;

  function resetForm() {
    setEmail('');
    setPassword('');
    setName('');
    setBirthday('');
    setMessage(null);
  }

  function switchMode(newMode) {
    setMode(newMode);
    setMessage(null);
  }

  async function handleSignIn(e) {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      await signIn(email, password);
      resetForm();
      onClose();
    } catch {
      // error is set via useAuth
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password) {
      setMessage('Please fill in name, email, and password.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      await signUp(email, password, name.trim(), birthday, createUser);
      resetForm();
      onClose();
    } catch {
      // error is set via useAuth
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    setSubmitting(true);
    setMessage(null);
    try {
      await signInWithGoogle(createUser);
      resetForm();
      onClose();
    } catch {
      // error is set via useAuth
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!email.trim()) {
      setMessage('Enter your email address.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      await resetPassword(email);
      setMessage('Password reset email sent. Check your inbox.');
    } catch {
      // error is set via useAuth
    } finally {
      setSubmitting(false);
    }
  }

  const displayError = error || message;

  return (
    <div className="blurred-app-state">
      <div className="modal-card auth-modal">
        <button className="sidebar-close-btn" onClick={onClose}>&times;</button>

        {mode === 'signin' && (
          <>
            <h2>sign in</h2>
            {displayError && <p className="auth-error">{displayError}</p>}
            <form onSubmit={handleSignIn}>
              <div className="input-group">
                <label className="input-label">Email</label>
                <input type="email" className="name-input" placeholder="your@email.com"
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="input-group">
                <label className="input-label">Password</label>
                <input type="password" className="name-input" placeholder="Password"
                  value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <button type="submit" className="btn-bank" style={{ marginTop: 15 }} disabled={submitting}>
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <button className="btn-google" onClick={handleGoogle} disabled={submitting}>
              Sign in with Google
            </button>
            <div className="auth-links">
              <button className="btn-link" onClick={() => switchMode('reset')}>Forgot password?</button>
              <button className="btn-link" onClick={() => switchMode('signup')}>Create an account</button>
            </div>
          </>
        )}

        {mode === 'signup' && (
          <>
            <h2>create account</h2>
            {displayError && <p className="auth-error">{displayError}</p>}
            <form onSubmit={handleSignUp}>
              <div className="input-group">
                <label className="input-label">Name</label>
                <input type="text" className="name-input" placeholder="Your name"
                  value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="input-group">
                <label className="input-label">Email</label>
                <input type="email" className="name-input" placeholder="your@email.com"
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="input-group">
                <label className="input-label">Password</label>
                <input type="password" className="name-input" placeholder="Choose a password"
                  value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <div className="input-group">
                <label className="input-label">Birthday</label>
                <input type="date" className="name-input"
                  value={birthday} onChange={e => setBirthday(e.target.value)} />
              </div>
              <button type="submit" className="btn-bank" style={{ marginTop: 15 }} disabled={submitting}>
                {submitting ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
            <button className="btn-google" onClick={handleGoogle} disabled={submitting}>
              Sign up with Google
            </button>
            <div className="auth-links">
              <button className="btn-link" onClick={() => switchMode('signin')}>Already have an account? Sign in</button>
            </div>
          </>
        )}

        {mode === 'reset' && (
          <>
            <h2>reset password</h2>
            {displayError && <p className="auth-error">{displayError}</p>}
            <form onSubmit={handleResetPassword}>
              <div className="input-group">
                <label className="input-label">Email</label>
                <input type="email" className="name-input" placeholder="your@email.com"
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <button type="submit" className="btn-bank" style={{ marginTop: 15 }} disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <div className="auth-links">
              <button className="btn-link" onClick={() => switchMode('signin')}>Back to sign in</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
